import React from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { WPVideo } from '@/data/wp/operations/queries/fragments/video';

declare type DivElementStyle = NonNullable<JSX.IntrinsicElements['div']['style']>;

export interface VideoProps {
  layout?: 'responsive' | 'fill' | 'fixed'
  objectFit?: DivElementStyle['objectFit'],
  objectPosition?: DivElementStyle['objectPosition'],
  controls?: boolean,
  autoplay?: boolean,
  loop?: boolean,
  mute?: boolean,
  media: WPVideo,
}

const StyledBox = styled(Box)({
  '&.layout-responsive': {
    'overflow': 'hidden',
    'paddingBottom': '56.25%',
    'position': 'relative',
    'height': 0,

    'iframe': {
      'position': 'absolute',
      'left': 0,
      'top': 0,
      'height': '100%',
      'width': '100%',
    }
  },

  '&.layout-fill': {
    'overflow': 'hidden',
    'position': 'absolute',
    'top': 0,
    'right': 0,
    'bottom': 0,
    'left': 0,

    'iframe': {
      'position': 'absolute',
      'left': 0,
      'top': 0,
      'height': '100%',
      'width': '100%',
    }
  }
});

/**
 * Given a youtube video url in one of the following formats
 * extract and return the video id. Returns false if nothig was found.
 *
 * Youtube Url format supported:
 * http://www.youtube.com/watch?v=0zM3nApSvMg&feature=feedrec_grec_index
 * http://www.youtube.com/user/IngridMichaelsonVEVO#p/a/u/1/QdK8U-VIH_o
 * http://www.youtube.com/v/0zM3nApSvMg?fs=1&amp;hl=en_US&amp;rel=0
 * http://www.youtube.com/watch?v=0zM3nApSvMg#t=0m10s
 * http://www.youtube.com/embed/0zM3nApSvMg?rel=0
 * http://www.youtube.com/watch?v=0zM3nApSvMg
 * http://youtu.be/0zM3nApSvMg
 */
const getVideoId = function(url: string) {
  var regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  var match = url.match(regExp);
  return (match&&match[7].length==11)? match[7] : false;
}

/**
  Renders a video.

  Right now the only video type supported is 'youtube'.

  The following info are all mandatory:
  - type: 'youtube'
  - videourl: url of the video (many formats accepted)
  - width & height: numeric original size needed to calculate the aspect ratio
  - title: video title

  Supported layouts:
  - responsive (Default): the video will scale the dimensions down for smaller viewports and scale up for larger viewports, respecting it's intrinsic aspect ratio (calculated width media.width and media.height).
      Ensure the parent element uses display: block in their stylesheet.
  - fixed: the video dimensions will not change as the viewport changes (no responsiveness) similar to the native iframe element.
  - fill: the video will stretch both width and height to the dimensions of the parent element, provided the parent element is relative.
      This is usually paired with the objectFit="cover" property.
      Ensure the parent element has position: relative in their stylesheet.

  Possibile improvements:
  - lazyloading with poster image
  - different sources (eg: vimeo, youtube, local mp4 w/ html5 video player)
  - use Youtube API instead of iframe to better interact with the player (eg: start/stop the video when is inside a slider or for page scroll / lazyloading. See: https://developers.google.com/youtube/iframe_api_reference)
*/
const Video: React.FC<VideoProps> = ({ layout = 'responsive', controls = true, autoplay = false, loop = false, mute = false, media }) => {
  const videoId = getVideoId(media.videourl);
  if (videoId) {
    // Note: objectFit, objectPosition would actually be ignored on an iframe
    return <StyledBox className={`layout-${layout}`}>
      <iframe
        width={media.width}
        height={media.height}
        src={`https://www.youtube.com/embed/${videoId}?rel=0&${!controls ? 'controls=0&': ''}${autoplay ? 'autoplay=1&': ''}${mute ? 'mute=1&': ''}${loop ? 'loop=1&': ''}`}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        title={media.title}
      />
    </StyledBox>
  } else {
    return null;
  }
}

export default Video;
